import React, { useEffect, useId, useRef, useState } from "react";
import { fetchTerritories } from "../api";
import { GeoLevel, TerritoryOption } from "../types";

interface TerritoryValue {
  level: GeoLevel;
  code: string;
}

interface TerritorySelectorProps {
  value: TerritoryValue;
  onChange: (value: TerritoryValue) => void;
}

const LEVEL_LABELS: Record<GeoLevel, string> = {
  senat: "Senátní obvod",
  kraj: "Kraj",
  okres: "Okres",
  obec: "Obec",
};

const LEVELS: GeoLevel[] = ["senat", "kraj", "okres", "obec"];

/** Threshold — levels with more options use the searchable combobox. */
const COMBOBOX_THRESHOLD = 50;

/** Strip Czech diacritics for accent-insensitive search. */
function stripDiacritics(text: string): string {
  return text.normalize("NFD").replace(/\p{Diacritic}/gu, "");
}

function matchesQuery(text: string, query: string): boolean {
  return stripDiacritics(text.toLowerCase()).includes(
    stripDiacritics(query.toLowerCase())
  );
}

// ─── Searchable Combobox ──────────────────────────────────────────────────────

interface ObecComboboxProps {
  options: TerritoryOption[];
  value: string;
  loading: boolean;
  label: string;
  onChange: (code: string) => void;
}

const ObecCombobox: React.FC<ObecComboboxProps> = ({
  options,
  value,
  loading,
  label,
  onChange,
}) => {
  const selectedOption = options.find((o) => o.code === value) ?? null;

  const [inputValue, setInputValue] = useState(
    selectedOption ? `${selectedOption.name} (${selectedOption.code})` : ""
  );
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const listId = useId();

  // Sync display value when external `value` prop changes (e.g. level switch).
  useEffect(() => {
    const opt = options.find((o) => o.code === value);
    setInputValue(opt ? `${opt.name} (${opt.code})` : "");
  }, [value, options]);

  const filteredOptions: TerritoryOption[] = isOpen
    ? options.filter((o) => matchesQuery(o.name, inputValue) || matchesQuery(o.code, inputValue))
    : [];

  function openDropdown() {
    setIsOpen(true);
    setActiveIndex(-1);
  }

  function closeAndRestore() {
    setIsOpen(false);
    setActiveIndex(-1);
    // Restore display value if user typed but didn't select.
    const opt = options.find((o) => o.code === value);
    setInputValue(opt ? `${opt.name} (${opt.code})` : "");
  }

  // Stable ref so the outside-click listener registered once on mount can
  // always call the latest closeAndRestore without re-registering on every render.
  const closeAndRestoreRef = useRef(closeAndRestore);
  closeAndRestoreRef.current = closeAndRestore;

  // Close on outside click — registered once on mount, cleaned up on unmount.
  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeAndRestoreRef.current();
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function selectOption(option: TerritoryOption) {
    setInputValue(`${option.name} (${option.code})`);
    setIsOpen(false);
    setActiveIndex(-1);
    onChange(option.code);
  }

  function clearSelection() {
    setInputValue("");
    setIsOpen(false);
    setActiveIndex(-1);
    onChange("");
    inputRef.current?.focus();
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputValue(e.target.value);
    setActiveIndex(-1);
    if (!isOpen) setIsOpen(true);
  }

  function scrollActiveItemIntoView(index: number) {
    if (!listRef.current) return;
    const item = listRef.current.children[index] as HTMLElement | undefined;
    if (item && typeof item.scrollIntoView === "function") {
      item.scrollIntoView({ block: "nearest" });
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        openDropdown();
        e.preventDefault();
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = Math.min(activeIndex + 1, filteredOptions.length - 1);
      setActiveIndex(next);
      scrollActiveItemIntoView(next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const next = Math.max(activeIndex - 1, 0);
      setActiveIndex(next);
      scrollActiveItemIntoView(next);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && filteredOptions[activeIndex]) {
        selectOption(filteredOptions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeAndRestore();
    }
  }

  const activeDescendantId =
    activeIndex >= 0 ? `${listId}-option-${activeIndex}` : undefined;

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <div style={{ position: "relative" }}>
        <input
          ref={inputRef}
          type="text"
          inputMode="search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          placeholder={loading ? "Načítám..." : `Hledat ${label.toLowerCase()}...`}
          value={inputValue}
          disabled={loading || options.length === 0}
          onChange={handleInputChange}
          onFocus={openDropdown}
          onKeyDown={handleKeyDown}
          className="dwx-input"
          style={{
            width: "100%",
            paddingRight: value ? "2.5rem" : "1rem",
            opacity: loading || options.length === 0 ? 0.5 : 1,
            minHeight: "44px",
          }}
          role="combobox"
          aria-label={`Vyberte ${label.toLowerCase()}`}
          aria-expanded={isOpen}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeDescendantId}
        />

        {/* Clear button — only when a value is selected */}
        {value && !loading && (
          <button
            type="button"
            onClick={clearSelection}
            aria-label="Zrušit výběr"
            style={{
              position: "absolute",
              right: "0.625rem",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "rgba(255,255,255,0.4)",
              padding: "0.25rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              transition: "color 0.15s ease",
              minWidth: "24px",
              minHeight: "24px",
            }}
          >
            {/* × icon */}
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M1 1l12 12M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Dropdown list */}
      {isOpen && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={`Výsledky pro ${label.toLowerCase()}`}
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "#0a0a0a",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "10px",
            maxHeight: "280px",
            overflowY: "auto",
            zIndex: 100,
            padding: "4px 0",
            margin: 0,
            listStyle: "none",
            boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
            // Prevent overflow on small viewports
            maxWidth: "100vw",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {filteredOptions.length === 0 ? (
            <li
              style={{
                padding: "0.75rem 1rem",
                color: "rgba(255,255,255,0.3)",
                fontFamily: "'Inter', sans-serif",
                fontSize: "0.8125rem",
                fontWeight: 300,
                userSelect: "none",
              }}
            >
              Žádná shoda
            </li>
          ) : (
            filteredOptions.map((opt, idx) => {
              const isActive = idx === activeIndex;
              const isSelected = opt.code === value;
              return (
                <li
                  key={opt.code}
                  id={`${listId}-option-${idx}`}
                  role="option"
                  aria-selected={isSelected}
                  onPointerDown={(e) => {
                    // Prevent input blur before click registers.
                    e.preventDefault();
                    selectOption(opt);
                  }}
                  style={{
                    padding: "0.6875rem 1rem",
                    cursor: "pointer",
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.875rem",
                    fontWeight: 300,
                    color: isSelected ? "#fafafa" : "rgba(255,255,255,0.75)",
                    background: isSelected
                      ? "rgba(255,255,255,0.1)"
                      : isActive
                      ? "rgba(255,255,255,0.05)"
                      : "transparent",
                    transition: "background 0.1s ease, color 0.1s ease",
                    // Ensure 44px min touch target height.
                    minHeight: "44px",
                    display: "flex",
                    alignItems: "center",
                    userSelect: "none",
                  }}
                >
                  {opt.name}
                  <span
                    style={{
                      marginLeft: "0.5rem",
                      color: "rgba(255,255,255,0.3)",
                      fontSize: "0.75rem",
                    }}
                  >
                    ({opt.code})
                  </span>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
};

// ─── TerritorySelector ────────────────────────────────────────────────────────

export const TerritorySelector: React.FC<TerritorySelectorProps> = ({ value, onChange }) => {
  const [options, setOptions] = useState<TerritoryOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    setLoadingOptions(true);
    fetchTerritories(value.level)
      .then((data) => {
        setOptions(data);
      })
      .catch(() => {
        setOptions([]);
      })
      .finally(() => {
        setLoadingOptions(false);
      });
  }, [value.level]);

  const handleLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLevel = e.target.value as GeoLevel;
    onChange({ level: newLevel, code: "" });
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ level: value.level, code: e.target.value });
  };

  const handleComboboxChange = (code: string) => {
    onChange({ level: value.level, code });
  };

  const useCombobox = options.length >= COMBOBOX_THRESHOLD || value.level === "obec";

  return (
    <div
      className="service-card"
      style={{
        borderRadius: "14px",
        padding: "1.25rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.875rem",
        position: "relative",
        zIndex: 30,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "0.875rem",
        }}
      >
        {/* ——— Level selector ——— */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
          <label
            className="eyebrow-label"
            style={{ marginLeft: "0.125rem" }}
          >
            Územní úroveň
          </label>
          <select
            value={value.level}
            onChange={handleLevelChange}
            className="dwx-select"
            style={{ minWidth: "10rem" }}
            aria-label="Vyberte územní úroveň"
          >
            {LEVELS.map((lvl) => (
              <option key={lvl} value={lvl}>
                {LEVEL_LABELS[lvl]}
              </option>
            ))}
          </select>
        </div>

        {/* ——— Code selector ——— */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.375rem",
            flex: 1,
            minWidth: "12rem",
          }}
        >
          <label
            className="eyebrow-label"
            style={{ marginLeft: "0.125rem" }}
          >
            {LEVEL_LABELS[value.level]}
          </label>

          {useCombobox ? (
            <ObecCombobox
              options={options}
              value={value.code}
              loading={loadingOptions}
              label={LEVEL_LABELS[value.level]}
              onChange={handleComboboxChange}
            />
          ) : (
            <select
              value={value.code}
              onChange={handleCodeChange}
              disabled={loadingOptions || options.length === 0}
              className="dwx-select"
              style={{
                width: "100%",
                opacity: loadingOptions || options.length === 0 ? 0.5 : 1,
              }}
              aria-label={`Vyberte ${LEVEL_LABELS[value.level].toLowerCase()}`}
            >
              <option value="">
                {loadingOptions ? "Načítám..." : `Vše (${LEVEL_LABELS[value.level]})`}
              </option>
              {options.map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {opt.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
    </div>
  );
};
